import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';

const Pricing = () => {
  const pricingTiers = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Pilots, churches, community orgs',
      features: [
        'Registration self-check embed',
        'Registration redirect flow',
        'Usage based messaging',
        'Basic registration check activity export only',
        'Limited level API calls'
      ],
      limits: ['No canvasser seats', 'No reminder Opt-ins', 'no advanced exports', 'no engagement logging', 'No shift tracking', '<1000 redirect checks'],
      buttonText: 'Current Plan',
      buttonVariant: 'secondary',
      isCurrent: true
    },
    {
      name: 'Community',
      price: '$99',
      period: '/month',
      description: 'C3 nonprofits running programs',
      features: [
        'Registration self-check embed',
        'Registration redirect flow',
        'Polling info & Voting method explanation',
        'Usage based messaging',
        'Polling location lookup',
        'Moderate level API calls'


      ],
      limits: ['Limited canvasser seats < 5', 'no audit/compliance exports', 'no field summaries', '<5,000 redirect checks', '<1000 Reminder opt-ins'],
      buttonText: 'Select Plan',
      buttonVariant: 'primary',
      isCurrent: false
    },
    {
      name: 'Field Pro',
      price: '$499',
      period: '/month',
      description: 'Core field operations',
      features: [
        'Registration self-check embed',
        'Start / end shift',
        'Registration redirect flow',
        'Usage based messaging',
        'Reminder opt-in',
        'Full canvasser workflows',
        'partner admin canvass mode',
        'Registration + field activity summaries',
        'Higher level API calls'


      ],
      limits: ['Limited canvasser seats < 25', 'No automated broadcasts', 'no compliance/audit exports',  '<25,000 redirect checks', '<5,000 Reminder opt-ins'],
      buttonText: 'Select Plan',
      buttonVariant: 'primary',
      isCurrent: false,
      isPopular: true
    },
    {
      name: 'Partner Growth',
      price: '$1250',
      period: '/month',
      description: 'Scaled organizations',
      features: [
        'Everything in Field Pro, plus:',
        'Registration redirect flow',
        'All partner-scoped inclusive compliance',
        'Usage-based messaging (broadcasts enabled)',
        'Export permissions',
        'High level API calls'
      ],
      limits: ['Limited canvasser seats < 50','Higher API limits required', '<15,000 Reminder opt-ins', 'admin approvals for exports', '<100,000 redirect checks'],
      buttonText: 'Select Plan',
      buttonVariant: 'primary',
      isCurrent: false
    },
    {
      name: 'Campaign',
      price: '$2500',
      period: '/month',
      description: 'PAC/Party organizations',
      features: [
        'Everything in Partner Growth',
        'Hard tenant isolation',
        'PAC/Party compliance firewall',
        'Additional logging',
        'Strict export controls',
        'FEC compliance ready',
        'Dedicated compliance support',
        'Enterprise level API calls'

      ],
      limits: ['Limited canvasser seats < 100',
        'Custom redirect check volume and schemas',
        'Stricter approvals', 'compliance controls', '<150,000 redirect checks', '<25,000 Reminder opt-ins'
      ],
      buttonText: 'Contact Sales',
      buttonVariant: 'primary',
      isCurrent: false
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-10 px-4 sm:px-6 lg:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Choose Your Access Level
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Select the perfect plan for your organization's needs
          </p>

          {/* Compliance Notice */}
          <motion.div
            className="inline-flex items-center justify-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Check className="w-4 h-4 mr-2" />
            Compliance-Safe Billing
          </motion.div>
        </motion.div>

        {/* Current Plan Indicator */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <span className="text-sm text-gray-500">Current Plan:</span>
          <span className="ml-2 text-sm font-semibold text-gray-900">Free</span>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6 mb-14"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              variants={cardVariants}
              whileHover="hover"
              className={`relative bg-white rounded-2xl shadow-lg border-2 ${tier.isPopular
                ? 'border-blue-500 ring-4 ring-blue-100'
                : 'border-gray-200'
                } overflow-hidden`}

            >
              {tier.isPopular && (
                <motion.div
                  className="absolute -top-2 left-[30%]  transform -translate-x-1/2 z-[1000000000]"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                >
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-1 rounded-full text-xs font-medium flex items-center">
                    Most Popular
                  </div>
                </motion.div>
              )}

              <div className="p-6 h-full flex flex-col">
                {/* Tier Name */}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {tier.name}
                </h3>

                {/* Price */}
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-lg text-gray-600 ml-1">{tier.period}</span>
                </div>

                {/* Description */}
                <p className="text-gray-600 mb-6 text-sm">
                  {tier.description}
                </p>

                {/* Features */}
                <div className="flex-grow mb-6">
                  <ul className="space-y-3">
                    {tier.features.map((feature, featureIndex) => (
                      <motion.li
                        key={featureIndex}
                        className="flex items-start"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + index * 0.1 + featureIndex * 0.05 }}
                      >
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </motion.li>
                    ))}
                  </ul>

                  {/* Limits */}
                  {tier.limits.length > 0 && (
                    <div className="mt-2 pt-2 -ml-3 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Limits:</h4>
                      <ul className="space-y-2">
                        {tier.limits.map((limit, limitIndex) => (
                          <motion.li
                            key={limitIndex}
                            className="flex items-start"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.7 + index * 0.1 + limitIndex * 0.05 }}
                          >
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0" />
                            <span className="text-gray-600 text-xs">{limit}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Button */}
                <motion.button
                  className={`w-full py-3 px-6 rounded-full font-semibold transition-all duration-300 ${tier.isCurrent
                    ? 'bg-gray-200 text-gray-700 cursor-not-allowed'
                    : tier.isPopular
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
                      : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  disabled={tier.isCurrent}
                  whileHover={!tier.isCurrent ? { scale: 1.05 } : {}}
                  whileTap={!tier.isCurrent ? { scale: 0.95 } : {}}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.3 }}
                >
                  {tier.buttonText}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Usage-Based Billing Model Section */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Usage-Based Billing Model</h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* What's Billed */}
            <div className='bg-red-100 border-red-500 border rounded-md p-5'>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Billed</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">$0.002 per redirect check over included volume (voter registration verification)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">$0.005 per opt-in over included volume (SMS reminders sent)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600"> Usage-based, billed separately via third-party providers (Messaging delivery (SMS/email))</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Tier-locked exports access (generation + approval required for advanced tiers)
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Canvasser accounts (if applicable)</span>
                </li>
              </ul>
            </div>

            {/* Never Billed On */}
            <div className='bg-lime-100 border-lime-500 border rounded-md p-5'>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Never Billed On</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Voter data storage</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Number of voters reached</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Basic analytics and reporting</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Admin Access Control Section */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Access Control</h2>
          <p className="text-gray-600">
            Your selected plan controls how many team members, canvassers, and reports your organization can access. <b>It does not affect what voters see.</b>
          </p>
        </motion.div>

        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Canvasser Feature Access by Tier</h2>

          <div className="overflow-x-auto mb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feature</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required Tier</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Start/End Shift</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600"><span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">Field Pro+</span></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Engagement Logging</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600"><span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">Field Pro+</span></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Export Data</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600"><span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800">Partner Growth+</span></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Reminder Broadcasts</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600"><span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">Tier-based</span></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Advanced Analytics</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600"><span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800">Partner Growth+</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-900 mb-2">Tenant Type vs. Access Tier</h3>
            <p className="text-sm text-amber-800">
              Your organization type (nonprofit, PAC, campaign) determines which access tiers are available to you.
              Some features may be restricted based on compliance requirements for your tenant type.
            </p>
          </div>
        </motion.div>

        <motion.div
          className="text-center text-gray-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          <p>All plans include our core compliance features and 24/7 support</p>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;
